// frontend/components/settings/CreateMemberModal.tsx

import { useState } from "react";
import { UserPlus, Shield, X } from "lucide-react";
import MaskedSecret from "../common/MaskedSecret";
import { ALL_DEPARTMENTS } from "../../constants/organization";

interface CreateMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: any) => Promise<boolean>;
  createdInfo: { loginId: string, tempPass: string, isReset?: boolean } | null;
}

// 신규 계정 발급 입력 폼 및 결과 렌더링
export default function CreateMemberModal({ isOpen, onClose, onCreate, createdInfo }: CreateMemberModalProps) {
  const [mId, setMId] = useState("");
  const [mName, setMName] = useState("");
  const [mEmail, setMEmail] = useState("");
  const [mDept, setMDept] = useState(ALL_DEPARTMENTS[0]);
  const [mRole, setMRole] = useState("USER");

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await onCreate({ loginId: mId, name: mName, email: mEmail, department: mDept, role: mRole });
    if (success) {
      // 성공 시 폼 초기화
      setMId(""); setMName(""); setMEmail(""); setMDept(ALL_DEPARTMENTS[0]); setMRole("USER");
    }
  };

  return (
    <div className="absolute inset-0 bg-white dark:bg-zinc-900 z-10 flex flex-col animate-in fade-in zoom-in-95 duration-200">
      <div className="flex items-center justify-between border-b dark:border-zinc-800 pb-3 mb-4">
        <p className="text-base font-bold text-gray-800 dark:text-zinc-200 flex items-center space-x-2">
          <Shield size={18} className="text-[#006DBA]"/> <span>신규 계정 발급 센터</span>
        </p>
        <button onClick={onClose} className="text-gray-400 hover:bg-gray-100 dark:hover:bg-zinc-800 p-1 rounded-md">
          <X size={18} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto pr-2 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-500 dark:text-gray-400">아이디 (사번) <span className="text-red-500">*</span></label>
            <input type="text" value={mId} onChange={(e) => setMId(e.target.value)} placeholder="예: user01" className="w-full p-2 text-sm bg-gray-50 dark:bg-zinc-950 border border-gray-200 dark:border-zinc-800 rounded-md outline-none focus:ring-1 focus:ring-[#006DBA]" required />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-500 dark:text-gray-400">이름 <span className="text-red-500">*</span></label>
            <input type="text" value={mName} onChange={(e) => setMName(e.target.value)} placeholder="예: 홍길동" className="w-full p-2 text-sm bg-gray-50 dark:bg-zinc-950 border border-gray-200 dark:border-zinc-800 rounded-md outline-none focus:ring-1 focus:ring-[#006DBA]" required />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-500 dark:text-gray-400">소속 부서 <span className="text-red-500">*</span></label>
            <select value={mDept} onChange={(e) => setMDept(e.target.value)} className="w-full p-2 text-sm bg-gray-50 dark:bg-zinc-950 border border-gray-200 dark:border-zinc-800 rounded-md outline-none focus:ring-1 focus:ring-[#006DBA] text-gray-900 dark:text-white" required>
              {ALL_DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-500 dark:text-gray-400">이메일 (선택)</label>
            <input type="email" value={mEmail} onChange={(e) => setMEmail(e.target.value)} placeholder="예: test@test.com" className="w-full p-2 text-sm bg-gray-50 dark:bg-zinc-950 border border-gray-200 dark:border-zinc-800 rounded-md outline-none focus:ring-1 focus:ring-[#006DBA]" />
          </div>
          
          <div className="col-span-2 space-y-1.5 pt-2">
            <label className="text-xs font-bold text-gray-500 dark:text-gray-400">계정 권한 부여 <span className="text-red-500">*</span></label>
            <div className="flex space-x-3 p-1">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input type="radio" name="role" value="USER" checked={mRole === "USER"} onChange={(e) => setMRole(e.target.value)} className="accent-[#006DBA]" />
                <span className="text-sm font-medium">내부직원 (USER)</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input type="radio" name="role" value="GUEST" checked={mRole === "GUEST"} onChange={(e) => setMRole(e.target.value)} className="accent-[#006DBA]" />
                <span className="text-sm font-medium">외부인 (GUEST)</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input type="radio" name="role" value="ADMIN" checked={mRole === "ADMIN"} onChange={(e) => setMRole(e.target.value)} className="accent-[#006DBA]" />
                <span className="text-sm font-medium text-red-500">최고관리자 (ADMIN)</span>
              </label>
            </div>
          </div>
        </div>
        
        <div className="pt-6">
          <button type="submit" className="w-full py-2.5 bg-[#006DBA] hover:bg-[#005A9E] text-white text-sm font-bold rounded-lg flex items-center justify-center space-x-2 transition-colors shadow-md">
            <UserPlus size={16}/> <span>계정 발급하기</span>
          </button>
        </div>
      </form>

      {/* 발급 성공 결과 렌더링 */}
      {createdInfo && !createdInfo.isReset && (
        <div className="mt-4 p-4 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900 rounded-lg text-sm space-y-2 text-green-800 dark:text-green-400 shadow-sm animate-in slide-in-from-bottom-2">
          <p className="font-bold flex items-center space-x-1"><span>계정이 성공적으로 발급되었습니다!</span></p>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <p>아이디: <span className="font-mono bg-white dark:bg-black/40 px-1.5 py-0.5 rounded border dark:border-zinc-800 ml-1">{createdInfo.loginId}</span></p>
            <p className="flex items-center">초기 암호: <MaskedSecret value={createdInfo.tempPass} className="text-red-500 ml-1" /></p>
          </div>
        </div>
      )}
    </div>
  );
}